import React, { useState } from 'react';
import {
  Box,
  Button,
  Typography,
  IconButton,
  Breadcrumbs,
  Menu,
  MenuItem,
  Divider,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import NavigateNext from '@mui/icons-material/NavigateNext';
import LogoutOutlined from '@mui/icons-material/LogoutOutlined';
import PersonOutlined from '@mui/icons-material/PersonOutlined';
import { useLocation, useNavigate, Link, NavLink } from 'react-router-dom';

import { useAppDispatch, useAppSelector } from '../hooks/redux';
import { signOut } from '../../features/auth/authSlice';
import { coursesApi } from '../../features/my-courses/coursesApi';
import { unitsApi } from '../../features/units/unitsApi';
import { lessonsApi } from '../../features/lessons/lessonsApi';
import { toggleNavDrawer } from '../store/uiSlice';
import { isFeatureEnabled } from '../libs/featureFlags';
import { brandLogo } from '../libs/brand';
import { APP_HEADER_HEIGHT_PX } from '../constants/layout';

import UserAvatar from './UserAvatar';

interface BreadcrumbItem {
  label?: string;
  path: string;
  entityType?: 'course' | 'unit' | 'lesson';
  entityId?: string;
}

type ResolvedBreadcrumbItem = BreadcrumbItem & {
  label: string;
};

const Header: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { user, isAuthenticated } = useAppSelector((state) => state.auth);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleAvatarClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleSignOut = async () => {
    try {
      await dispatch(signOut()).unwrap();
      navigate('/login');
    } catch (error) {
      console.error('Sign out error:', error);
    }
    handleMenuClose();
  };

  const handleSignIn = () => {
    navigate('/login');
  };

  // Determine if we're inside the curriculum layout (course/unit/lesson hierarchy)
  const pathSegments = location.pathname.split('/').filter(Boolean);
  const isInCurriculum =
    pathSegments[0] === 'curriculum' && pathSegments[1] === 'courses' && pathSegments.length >= 3;

  // Generate breadcrumbs from current path, respecting the hierarchical route structure
  const generateBreadcrumbs = (): BreadcrumbItem[] => {
    const breadcrumbs: BreadcrumbItem[] = [];

    // Handle different route patterns
    if (pathSegments.length === 0) {
      return breadcrumbs;
    }

    // Add app-only ancestors for signed-in users once they are past the dashboard root.
    if (pathSegments[0] === 'curriculum') {
      if (pathSegments.length === 1) {
        return breadcrumbs;
      }

      if (isAuthenticated) {
        breadcrumbs.push({ label: 'Dashboard', path: '/curriculum' });
      }

      // Handle hierarchical routes
      if (pathSegments[1] === 'courses') {
        if (isAuthenticated) {
          breadcrumbs.push({ label: 'My Courses', path: '/curriculum/courses' });
        }

        // If we have a courseId
        if (pathSegments.length >= 3 && pathSegments[2]) {
          const courseId = pathSegments[2];
          breadcrumbs.push({
            path: `/curriculum/courses/${courseId}`,
            entityType: 'course',
            entityId: courseId,
          });

          // If we have units
          if (pathSegments.length >= 5 && pathSegments[3] === 'units' && pathSegments[4]) {
            const unitId = pathSegments[4];
            breadcrumbs.push({
              path: `/curriculum/courses/${courseId}/units/${unitId}`,
              entityType: 'unit',
              entityId: unitId,
            });

            // If we have lessons
            if (pathSegments.length >= 7 && pathSegments[5] === 'lessons' && pathSegments[6]) {
              const lessonId = pathSegments[6];
              breadcrumbs.push({
                path: `/curriculum/courses/${courseId}/units/${unitId}/lessons/${lessonId}`,
                entityType: 'lesson',
                entityId: lessonId,
              });
            }
          }
        }
      } else if (pathSegments[1] === 'standards') {
        if (isAuthenticated) {
          breadcrumbs.push({ label: 'Standards', path: '/curriculum/standards' });
          if (pathSegments[2] === 'find') {
            breadcrumbs.push({ label: 'Find Standards', path: '/curriculum/standards/find' });
          }
        }
      } else if (pathSegments[1] === 'rubrics') {
        if (isAuthenticated) {
          breadcrumbs.push({ label: 'Rubrics', path: '/curriculum/rubrics' });
        }
      } else if (pathSegments[1] === 'units') {
        // Handle orphan unit access (shared on unit but not course)
        if (isAuthenticated) {
          breadcrumbs.push({ label: 'Shared with Me', path: '/curriculum/courses?tab=shared' });
        }
        if (pathSegments.length >= 3 && pathSegments[2]) {
          const unitId = pathSegments[2];
          breadcrumbs.push({
            path: `/curriculum/units/${unitId}`,
            entityType: 'unit',
            entityId: unitId,
          });

          // Handle orphan unit + lesson path
          if (pathSegments.length >= 5 && pathSegments[3] === 'lessons' && pathSegments[4]) {
            const lessonId = pathSegments[4];
            breadcrumbs.push({
              path: `/curriculum/units/${unitId}/lessons/${lessonId}`,
              entityType: 'lesson',
              entityId: lessonId,
            });
          }
        }
      } else if (pathSegments[1] === 'lessons') {
        // Handle orphan lesson access (shared on lesson but not unit/course)
        if (isAuthenticated) {
          breadcrumbs.push({ label: 'Shared with Me', path: '/curriculum/courses?tab=shared' });
        }
        if (pathSegments.length >= 3 && pathSegments[2]) {
          const lessonId = pathSegments[2];
          breadcrumbs.push({
            path: `/curriculum/lessons/${lessonId}`,
            entityType: 'lesson',
            entityId: lessonId,
          });
        }
      }
    }

    return breadcrumbs;
  };

  const baseBreadcrumbs = generateBreadcrumbs();

  // Extract entity IDs from breadcrumbs for API calls
  const courseId = baseBreadcrumbs.find((b) => b.entityType === 'course')?.entityId;
  const unitId = baseBreadcrumbs.find((b) => b.entityType === 'unit')?.entityId;
  const lessonId = baseBreadcrumbs.find((b) => b.entityType === 'lesson')?.entityId;

  // Fetch entity data using RTK Query hooks
  const { data: courseData } = coursesApi.useGetCourseByIdQuery(courseId!, {
    skip: !courseId,
  });
  const { data: unitData } = unitsApi.useGetUnitByIdQuery(unitId!, {
    skip: !unitId,
  });
  const { data: lessonData } = lessonsApi.useGetLessonByIdQuery(lessonId!, {
    skip: !lessonId,
  });

  const getResolvedEntityLabel = (crumb: BreadcrumbItem) => {
    if (crumb.entityType === 'course' && courseData) {
      return courseData.title;
    }
    if (crumb.entityType === 'unit' && unitData) {
      return unitData.title;
    }
    if (crumb.entityType === 'lesson' && lessonData) {
      return lessonData.title;
    }
    return null;
  };

  // Resolve entity labels before rendering so UUIDs never appear in breadcrumbs.
  let encounteredUnresolvedEntity = false;
  const resolvedBreadcrumbs = baseBreadcrumbs.reduce<ResolvedBreadcrumbItem[]>(
    (resolved, crumb) => {
      if (encounteredUnresolvedEntity) {
        return resolved;
      }

      if (!crumb.entityType) {
        if (crumb.label) resolved.push(crumb as ResolvedBreadcrumbItem);
        return resolved;
      }

      const label = getResolvedEntityLabel(crumb);
      if (label) {
        resolved.push({ ...crumb, label });
      } else {
        encounteredUnresolvedEntity = true;
      }

      return resolved;
    },
    []
  );
  const breadcrumbs =
    resolvedBreadcrumbs.length === 1 && resolvedBreadcrumbs[0].path === '/curriculum'
      ? []
      : resolvedBreadcrumbs;

  return (
    <Box
      component="header"
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        boxSizing: 'border-box',
        height: APP_HEADER_HEIGHT_PX,
        px: 3,
        py: 0,
        position: 'relative',
        backgroundColor: 'background.paper',
        borderBottom: '1px solid',
        borderColor: 'grey.200',
      }}
    >
      {/* Left side - Hamburger (mobile, curriculum only) + Logo/Brand */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          flex: '0 0 auto',
        }}
      >
        {isInCurriculum && (
          <IconButton
            aria-label="Open navigation"
            onClick={() => dispatch(toggleNavDrawer())}
            sx={{ display: { xs: 'inline-flex', sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
        )}
        <Box
          component={Link}
          to={isAuthenticated ? '/curriculum' : '/'}
          aria-label={isAuthenticated ? 'Go to curriculum dashboard' : 'Go to home'}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            textDecoration: 'none',
            '&:hover': {
              opacity: 0.8,
            },
          }}
        >
          {/*<Box
            component="img"
            src="/Commonly Logo.png"
            alt="Commonly"
            sx={{
              height: { xs: 24, sm: 26, md: 28 },
              maxHeight: '100%',
              width: 'auto',
              display: 'block',
              marginTop: -2,
              marginBottom: -2,
            }}
          />
          <Box
            component="img"
            src="/currikulo logo.png"
            alt="currikulo"
            sx={{
              height: { xs: 24, sm: 26, md: 28 },
              maxHeight: '100%',
              width: 'auto',
              display: 'block',
              marginTop: -2,
              marginBottom: -2,
            }}
          />
          */}
          <Box
            component="img"
            src={brandLogo.src}
            alt={brandLogo.alt}
            sx={{
              height: { xs: 24, sm: 26, md: 28 },
              maxHeight: '100%',
              width: 'auto',
              display: 'block',
              marginTop: -2,
              marginBottom: -2,
            }}
          />
        </Box>
      </Box>

      {/* Breadcrumbs (md+ only) - left-aligned next to the logo, fills toward the actions */}
      <Box
        sx={{
          flex: '1 1 auto',
          minWidth: 0,
          display: { xs: 'none', md: 'flex' },
          justifyContent: 'flex-start',
          overflow: 'hidden',
          ml: 3,
          mr: 2,
        }}
      >
        {breadcrumbs.length > 0 && (
          <Breadcrumbs
            separator={<NavigateNext fontSize="small" sx={{ color: 'text.disabled' }} />}
            sx={{
              minWidth: 0,
              '& .MuiBreadcrumbs-ol': {
                alignItems: 'center',
                flexWrap: 'nowrap',
              },
              '& .MuiBreadcrumbs-li': {
                minWidth: 0,
              },
            }}
          >
            {breadcrumbs.map((crumb, index) => {
              const isLast = index === breadcrumbs.length - 1;

              if (isLast) {
                return (
                  <Typography
                    key={crumb.path}
                    variant="body2"
                    title={crumb.label}
                    sx={{
                      color: 'text.primary',
                      fontWeight: 500,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      maxWidth: { md: 160, lg: 240 },
                    }}
                  >
                    {crumb.label}
                  </Typography>
                );
              }
              return (
                <NavLink key={crumb.path} to={crumb.path} style={{ textDecoration: 'none' }}>
                  {({ isActive }) => (
                    <Box
                      title={crumb.label}
                      sx={{
                        color: isActive ? 'text.primary' : 'text.secondary',
                        fontSize: '0.875rem',
                        fontWeight: isActive ? 500 : 400,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        maxWidth: { md: 140, lg: 200 },
                        '&:hover': {
                          color: 'primary.main',
                          textDecoration: 'underline',
                        },
                      }}
                    >
                      {crumb.label}
                    </Box>
                  )}
                </NavLink>
              );
            })}
          </Breadcrumbs>
        )}
      </Box>

      {/* Right side - Actions */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          flex: '0 0 auto',
        }}
      >
        {isAuthenticated ? (
          <>
            <UserAvatar
              userId={user?.id}
              name={user?.user_metadata?.full_name ?? user?.user_metadata?.name ?? null}
              email={user?.email}
              src={user?.user_metadata?.avatar_url ?? user?.user_metadata?.picture ?? null}
              size={36}
              sx={{
                ml: 1,
                cursor: 'pointer',
                '&:hover': {
                  filter: 'brightness(0.92)',
                },
              }}
              onClick={handleAvatarClick}
            />

            <Menu
              anchorEl={anchorEl}
              open={open}
              onClose={handleMenuClose}
              sx={{
                mt: 1,
              }}
            >
              {isFeatureEnabled('profile') && (
                <MenuItem
                  onClick={() => {
                    navigate('/profile');
                    handleMenuClose();
                  }}
                >
                  <PersonOutlined sx={{ mr: 1 }} /> Profile
                </MenuItem>
              )}
              {isFeatureEnabled('profile') && <Divider sx={{ my: 0.5 }} />}
              <MenuItem onClick={handleSignOut} sx={{ color: 'error.main' }}>
                <LogoutOutlined sx={{ mr: 1 }} /> Logout
              </MenuItem>
            </Menu>
          </>
        ) : (
          <Button variant="outlined" size="small" onClick={handleSignIn} sx={{ ml: 1 }}>
            Sign In
          </Button>
        )}
      </Box>
    </Box>
  );
};

export default Header;
